import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

/**
 * Замечает, что на сервере лежит новая сборка, и предлагает обновиться.
 *
 * ЗАЧЕМ (05.09.2026). У index.html НЕТ заголовка Cache-Control ни на одном
 * из хостов — проверено curl'ом: только etag и last-modified. Браузер
 * и Telegram решают сами, сколько его держать, а вместе с ним держат ссылку
 * на старый бандл. Файлы assets/*.js уникальны по имени и кэшируются вечно
 * правильно, а вот index.html кэшировать нельзя вообще.
 *
 * Из-за этого выкаченный код мог не доезжать до человека, и он видел старое
 * приложение, не понимая почему. За два дня это случилось дважды.
 *
 * Настроить заголовки на хостинге нельзя: обе сборки живут на Timeweb App
 * Platform под их собственным Caddy, конфиг не наш. Поэтому решаем в самом
 * приложении — работает независимо от хостинга и от политики кэша.
 *
 * Как: тянем index.html МИМО КЭША и сравниваем имя главного бандла с тем,
 * что сейчас выполняется. Разные — значит вышло обновление.
 *
 * По хартии ненавязчивости: полоска, а не окно; появляется один раз
 * и не мигает; закрыть можно, и до следующей сборки не вернётся.
 */

const CHECK_INTERVAL_MS = 30 * 60 * 1000; // раз в полчаса, если приложение открыто

/** Имя бандла, который выполняется прямо сейчас */
function currentBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  const m = el?.src.match(/\/assets\/(index-[^/"]+\.js)/);
  return m ? m[1] : null;
}

async function fetchLatestBundle(): Promise<string | null> {
  try {
    // no-store обязателен: иначе запрос уйдёт в тот же кэш, из-за которого
    // всё и затевалось, и мы будем сравнивать старое со старым.
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/\/assets\/(index-[^/"]+\.js)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * Что нового в сборке, которая лежит на сервере (06.10.2026, запрос Славы:
 * «в кратце упоминать, что именно — пользователь должен заинтересоваться»).
 *
 * Файл public/whats-new.json выкатывается вместе со сборкой, поэтому СТАРАЯ версия
 * приложения читает описание НОВОЙ — ровно в тот момент, когда предлагает обновиться.
 * Текст пишется руками при выкладке: одна фраза про пользу человеку, не про код.
 * Файла нет или он битый — плашка остаётся прежней, без подробностей.
 */
async function fetchWhatsNew(): Promise<string | null> {
  try {
    const res = await fetch(`/whats-new.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    // На хостинге одностраничных приложений отсутствующий файл отдаётся как index.html
    // с кодом 200 — тогда json() бросит исключение, и мы тихо вернём null.
    const data = await res.json();
    const text = typeof data?.text === 'string' ? data.text.trim() : '';
    return text ? text.slice(0, 140) : null;
  } catch {
    return null;
  }
}

export function UpdateBanner() {
  const [outdated, setOutdated] = useState(false);
  const [news, setNews] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const mine = currentBundle();
    // В деве бандла с хэшем нет — проверять нечего.
    if (!mine) return;

    let stopped = false;

    async function check() {
      if (stopped) return;
      const latest = await fetchLatestBundle();
      if (latest && latest !== mine) {
        setOutdated(true);
        fetchWhatsNew().then((t) => { if (!stopped) setNews(t); });
      }
    }

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    // Возврат в приложение — самый вероятный момент, когда обновление
    // успело выйти: человек свернул, мы выкатили, он открыл снова.
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!outdated || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className="fixed top-0 inset-x-0 z-[60] px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2"
      >
        <div
          className="max-w-md mx-auto flex items-center gap-2.5 px-3 py-2 rounded-xl border backdrop-blur-xl"
          style={{ borderColor: 'rgba(16,229,142,0.4)', background: 'rgba(11,15,18,0.92)' }}
        >
          <RefreshCw size={14} className="text-emerald-glow shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-ink">{news ? 'Вышло обновление' : 'Вышло обновление приложения'}</div>
            {news && <div className="text-[11px] text-emerald-glow leading-snug mt-0.5">{news}</div>}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="shrink-0 px-2.5 py-1 rounded-lg bg-emerald-glow text-bg text-[11px] font-bold"
          >
            Обновить
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="shrink-0 text-[11px] text-ink-dim px-1"
          >
            позже
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
